import React, { useContext, useRef, useState, useEffect } from 'react';
import classnames from 'clsx';
import type { ToolbarChildrenProps } from 'draft-js-inline-toolbar-plugin';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import StylesContext from '../StylesContext';
import type { ToolbarProps } from '../../RichTextBox.types';
import type { ScrollerProps } from './types';
import semanticClassNames from '../../RichTextBox.semanticClassNames';
import Separator from './separator/Separator';
import Scroller from './scroller/Scroller';
import BoldButton from './buttons/BoldButton';
import UnderlineButton from './buttons/UnderlineButton';
import ItalicButton from './buttons/ItalicButton';
import TextSizeButtonStatic from './buttons/TextSizeButton/Static';
import TextSizeButtonInline from './buttons/TextSizeButton/Inline';
import OrderedListButton from './buttons/OrderedListIcon';
import UnorderedListButton from './buttons/UnorderedListIcon';
import LinkButtonInline from './buttons/LinkButton/Inline';
import LinkButtonStatic from './buttons/LinkButton/Static';
import TextAlignButtonsStatic from './buttons/TextAlignButtons/Static';
import TextAlignButtonsInline from './buttons/TextAlignButtons/Inline';

import styles from './toolbar.scss';
import { TestIds } from '../../constants';
import { calculateNextIndex, calculatePreviousIndex } from './utils';

const Toolbar: React.FC<ToolbarProps> = ({
  type,
  ToolbarInstance,
  allowLinks,
  linkModalId,
  openMobileLinkModalTemp,
  closeMobileLinkModalTemp,
  onOverrideDialogClose,
  onOverrideDialogOpen,
  scopedClassName,
  lastSelectionBeforeKeyboardNavigation,
  direction,
  linkModalButtonRef,
  rootId,
  translations,
  shouldShowTooltips,
}) => {
  const [scrollerDirection, setScrollerDirection] =
    useState<ScrollerProps['direction']>('right');
  const [scrollerVisible, setScrollerVisible] = useState(false);

  const boldRef = useRef<HTMLButtonElement>(null);
  const italicRef = useRef<HTMLButtonElement>(null);
  const underlineRef = useRef<HTMLButtonElement>(null);
  const textSizeRef = useRef<HTMLButtonElement>(null);
  const textAlignRef = useRef<HTMLButtonElement>(null);
  const orderedListRef = useRef<HTMLButtonElement>(null);
  const unorderedListRef = useRef<HTMLButtonElement>(null);
  const linkRef = useRef<HTMLButtonElement>(null);

  const buttonRefs = React.useMemo(
    () => ({
      bold: boldRef,
      italic: italicRef,
      underline: underlineRef,
      textSize: textSizeRef,
      textAlign: textAlignRef,
      orderedList: orderedListRef,
      unorderedList: unorderedListRef,
      link: linkRef,
    }),
    [],
  );

  const stylesContext = useContext(StylesContext);
  useEffect(() => {
    // The styles should be loaded async, otherwise the "scrollWidth" and the "clientWidth" are equal and the scroll button does not render
    async function loadStyles() {
      const importStaticCss = () =>
        import('draft-js-static-toolbar-plugin/lib/plugin.css');
      const importInlineCss = () =>
        import('draft-js-inline-toolbar-plugin/lib/plugin.css');

      if (type === 'top' || type === 'bottom') {
        await importStaticCss();
      } else {
        await importInlineCss();
      }

      if (
        actionButtonsContainerRef.current!.scrollWidth >
        actionButtonsContainerRef.current!.clientWidth
      ) {
        setScrollerVisible(true);
      }
    }

    void loadStyles();
  }, [type]);

  const actionButtonsContainerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [activeButtonIndex, setActiveButtonIndex] = useState(0);

  const scroll = () => {
    const element = actionButtonsContainerRef!.current!;
    const scrollWidth = actionButtonsContainerRef!.current!.scrollWidth;

    if (scrollerDirection === 'right') {
      if (direction === 'rtl') {
        element.scrollLeft -= scrollWidth;
      } else {
        element.scrollLeft += scrollWidth;
      }

      setScrollerDirection('left');
    } else {
      if (direction === 'rtl') {
        element.scrollLeft += scrollWidth;
      } else {
        element.scrollLeft -= scrollWidth;
      }

      setScrollerDirection('right');
    }
  };

  const TextAlignButtons = (externalProps: ToolbarChildrenProps) => {
    return type === 'inline' ? (
      <TextAlignButtonsInline
        shouldShowTooltips={shouldShowTooltips}
        {...externalProps}
        rootId={rootId}
        translations={translations}
        ref={buttonRefs.textAlign}
      />
    ) : (
      <TextAlignButtonsStatic
        shouldShowTooltips={shouldShowTooltips}
        {...externalProps}
        rootId={rootId}
        translations={translations}
        ref={buttonRefs.textAlign}
      />
    );
  };

  const TextSizeButtons = (externalProps: ToolbarChildrenProps) => {
    return type === 'inline' ? (
      <TextSizeButtonInline
        {...externalProps}
        shouldShowTooltips={shouldShowTooltips}
        lastSelectionBeforeKeyboardNavigation={
          lastSelectionBeforeKeyboardNavigation
        }
        rootId={rootId}
        translations={translations}
        ref={buttonRefs.textSize}
      />
    ) : (
      <TextSizeButtonStatic
        {...externalProps}
        shouldShowTooltips={shouldShowTooltips}
        lastSelectionBeforeKeyboardNavigation={
          lastSelectionBeforeKeyboardNavigation
        }
        rootId={rootId}
        translations={translations}
        ref={buttonRefs.textSize}
      />
    );
  };

  const LinkButton = (externalProps: ToolbarChildrenProps) => {
    return type === 'inline' ? (
      <LinkButtonInline
        {...externalProps}
        shouldShowTooltips={shouldShowTooltips}
        linkModalButtonRef={linkModalButtonRef}
        translations={translations}
        linkModalId={linkModalId}
        ref={buttonRefs.link}
      />
    ) : (
      <LinkButtonStatic
        {...externalProps}
        shouldShowTooltips={shouldShowTooltips}
        linkModalButtonRef={linkModalButtonRef}
        type={type}
        linkModalId={linkModalId}
        openMobileLinkModalTemp={openMobileLinkModalTemp}
        closeMobileLinkModalTemp={closeMobileLinkModalTemp}
        onOverrideDialogClose={onOverrideDialogClose}
        onOverrideDialogOpen={onOverrideDialogOpen}
        scopedClassName={scopedClassName}
        translations={translations}
        ref={buttonRefs.link}
      />
    );
  };

  const handleKeyboardNav = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const buttons = Object.values(buttonRefs).filter(ref => ref.current);
      const isButtonActive = buttons.find(
        ref => ref.current === document.activeElement,
      );
      if (!isButtonActive) {
        return;
      }
      e.preventDefault();

      const arrowDirectionNext =
        direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';

      let newIndex: number;
      if (e.key === arrowDirectionNext) {
        newIndex = calculateNextIndex(activeButtonIndex, buttons.length);
      } else {
        newIndex = calculatePreviousIndex(activeButtonIndex, buttons.length);
      }

      buttons.forEach((btn, idx) => {
        (btn.current as HTMLButtonElement).tabIndex = idx === newIndex ? 0 : -1;
      });

      (buttons[newIndex].current as HTMLButtonElement).focus();
      setActiveButtonIndex(newIndex);

      const container = actionButtonsContainerRef.current;
      const activeButton = buttons[newIndex].current as HTMLElement;

      if (container && activeButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        if (buttonRect.right > containerRect.right) {
          container.scrollLeft += buttonRect.right - containerRect.right + 10;
          setScrollerDirection('left');
        } else if (buttonRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - buttonRect.left + 10;
          setScrollerDirection('right');
        }
      }
    }
  };

  useEffect(() => {
    if (!toolbarRef.current) {
      return;
    }

    const buttons = Object.values(buttonRefs).filter(ref => ref.current);

    buttons.forEach((button, index) => {
      (button.current as HTMLButtonElement).tabIndex = index === 0 ? 0 : -1;
    });
  }, [buttonRefs]);

  return (
    <ToolbarInstance>
      {(externalProps: ToolbarChildrenProps) => (
        <div
          role="toolbar"
          data-testid={TestIds.toolbar}
          className={classnames(
            styles.toolbar,
            formatClassNames(semanticClassNames.toolbar),
            {
              [styles.hovered]: stylesContext.hovered,
              [styles.focused]: stylesContext.focused,
              [styles.error]: stylesContext.error,
              [styles.disabled]: stylesContext.disabled,
              [styles.bottomToolbar]: type === 'bottom',
              [styles.topToolbar]: type === 'top',
              [styles.inlineToolbar]: type === 'inline',
            },
          )}
          ref={toolbarRef}
          onKeyDown={handleKeyboardNav}
        >
          <div
            ref={actionButtonsContainerRef}
            className={styles.actionButtonsContainer}
          >
            {[
              <BoldButton
                {...externalProps}
                shouldShowTooltips={shouldShowTooltips}
                lastSelectionBeforeKeyboardNavigation={
                  lastSelectionBeforeKeyboardNavigation
                }
                translations={translations}
                ref={buttonRefs.bold}
              />,
              <ItalicButton
                {...externalProps}
                shouldShowTooltips={shouldShowTooltips}
                lastSelectionBeforeKeyboardNavigation={
                  lastSelectionBeforeKeyboardNavigation
                }
                translations={translations}
                ref={buttonRefs.italic}
              />,
              <UnderlineButton
                {...externalProps}
                shouldShowTooltips={shouldShowTooltips}
                lastSelectionBeforeKeyboardNavigation={
                  lastSelectionBeforeKeyboardNavigation
                }
                translations={translations}
                ref={buttonRefs.underline}
              />,
              TextSizeButtons(externalProps),
              <Separator />,
              TextAlignButtons(externalProps),
              <OrderedListButton
                {...externalProps}
                shouldShowTooltips={shouldShowTooltips}
                lastSelectionBeforeKeyboardNavigation={
                  lastSelectionBeforeKeyboardNavigation
                }
                translations={translations}
                ref={buttonRefs.orderedList}
              />,
              <UnorderedListButton
                {...externalProps}
                shouldShowTooltips={shouldShowTooltips}
                lastSelectionBeforeKeyboardNavigation={
                  lastSelectionBeforeKeyboardNavigation
                }
                translations={translations}
                ref={buttonRefs.unorderedList}
              />,
              ...(allowLinks ? [<Separator />, LinkButton(externalProps)] : []),
            ].map((button, index) => (
              <div key={index} className={styles.buttonWrapper}>
                {button}
              </div>
            ))}
          </div>
          {scrollerVisible && (
            <Scroller direction={scrollerDirection} onClick={scroll} />
          )}
        </div>
      )}
    </ToolbarInstance>
  );
};

export default Toolbar;
